import type { TableMetadataType } from "@/types/tableMetadata";

/** The synthetic column that carries the discrepancy marker. */
export const DISCREPANCY_COLUMN = "discrepancies";

/**
 * Datasets where a discrepancy marker is meaningful.
 *
 * `subjects_overview` is subject-level (one row is one participant); the rest
 * are record-level and expose their own primary key, which is what a marker
 * keys on. A dataset absent here simply never offers the column.
 */
export const MARKER_DATASETS = new Set([
  "subjects_overview",
  "adverse_events",
  "labs",
  "assessments",
  "imaging",
  "questionnaires",
  "conmed",
]);

/**
 * Append the marker column to a dataset's metadata.
 *
 * The column is synthetic — no backend field, no filter values — but it is
 * injected here rather than rendered outside the table so it behaves like every
 * other column: it appears in the column picker, can be hidden or reordered,
 * and persists in a saved workspace. Column state is derived from
 * `metadata.columns`, and a column absent from that list is stripped when a
 * workspace loads, which is why this is the right seam.
 */
function appendColumn<T extends { columns?: unknown[] }>(target: T): T {
  const columns = target.columns ?? [];
  const alreadyPresent = columns.some((column) =>
    typeof column === "string"
      ? column === DISCREPANCY_COLUMN
      : (column as { field?: string })?.field === DISCREPANCY_COLUMN,
  );
  if (alreadyPresent) return target;
  return {
    ...target,
    columns: [
      ...columns,
      { field: DISCREPANCY_COLUMN, label: "Discrepancies" },
    ],
  };
}

/**
 * Append the marker column to a dataset's metadata, including any per-page
 * slices of a tabbed dashboard.
 *
 * The column is synthetic — no backend field, no filter values — but it is
 * injected into metadata rather than rendered outside the table so it behaves
 * like every other column: it appears in the column picker, can be hidden or
 * reordered, and persists in a saved workspace. Column state is normalised
 * against `metadata.columns`, and a column absent from that list is stripped,
 * which is why this is the right seam.
 */
export function withDiscrepancyColumn(
  metadata: TableMetadataType | undefined,
  datasetName: string,
  enabled: boolean,
): TableMetadataType | undefined {
  if (!metadata || !enabled || !MARKER_DATASETS.has(datasetName)) {
    return metadata;
  }

  const augmented = appendColumn(
    metadata as TableMetadataType & {
      columns?: unknown[];
    },
  );

  const pages = (
    metadata as { pages?: Record<string, { columns?: unknown[] }> }
  ).pages;
  if (!pages) return augmented as TableMetadataType;

  return {
    ...augmented,
    pages: Object.fromEntries(
      Object.entries(pages).map(([key, page]) => [key, appendColumn(page)]),
    ),
  } as TableMetadataType;
}
